import { useCallback, useEffect, useState } from "react";
import {
  fetchFoodSupplySourceStatus,
  runFoodSupplyIngestion,
  runFoodSupplyTrendCalculation,
} from "../../../../api/foodApi";

const FoodSupplyIngestionPage = ({ onClose }) => {
  const [status, setStatus] = useState(null);
  const [city, setCity] = useState("Medellín");
  const [department, setDepartment] = useState("Antioquia");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [limit, setLimit] = useState(250);
  const [dryRun, setDryRun] = useState(true);
  const [useMock, setUseMock] = useState(true);
  const [running, setRunning] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [trendResult, setTrendResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await fetchFoodSupplySourceStatus());
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Unable to load supply source status.");
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleIngest = async () => {
    try {
      setRunning(true);
      setIngestResult(
        await runFoodSupplyIngestion({
          sourceKey: "DANE_SIPSA_SUPPLY",
          city,
          department,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          limit: Number(limit) || 250,
          dryRun,
          useMock,
        })
      );
      if (!dryRun) await loadStatus();
    } catch (error) {
      setErrorMessage(error.message || "Supply ingestion failed.");
    } finally {
      setRunning(false);
    }
  };

  const handleTrends = async () => {
    try {
      setRunning(true);
      setTrendResult(
        await runFoodSupplyTrendCalculation({
          city,
          department,
          daysBack: 30,
          dryRun,
        })
      );
    } catch (error) {
      setErrorMessage(error.message || "Trend calculation failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-page food-supply-ingestion-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>SIPSA Supply Ingestion</h1>
          <p className="muted">Abastecimiento volume — manual runs only.</p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <section className="panel-card">
        <h2>Configuration</h2>
        {status ? (
          <ul>
            <li>Health: {status.source?.healthStatus}</li>
            <li>Endpoint: {status.configuration?.endpointConfigured ? "Yes" : "No"}</li>
            <li>Mock: {status.configuration?.useMock ? "Yes" : "No"}</li>
          </ul>
        ) : null}
        <p className="muted">
          Set <code>SIPSA_SUPPLY_ENDPOINT_URL</code> or <code>SIPSA_SUPPLY_MOCK=true</code>.
        </p>
      </section>

      <section className="panel-card">
        <div className="food-normalization-page__control-row">
          <label>
            City <input value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <label>
            Department{" "}
            <input value={department} onChange={(e) => setDepartment(e.target.value)} />
          </label>
          <label>
            Date from <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label>
            Date to <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
          <label>
            Limit <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} />
          </label>
          <label className="food-comparison-filters__checkbox">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Dry run
          </label>
          <label className="food-comparison-filters__checkbox">
            <input type="checkbox" checked={useMock} onChange={(e) => setUseMock(e.target.checked)} />
            Use mock data
          </label>
          <button type="button" className="btn btn--primary" disabled={running} onClick={handleIngest}>
            Run supply ingestion
          </button>
          <button type="button" className="btn btn--ghost" disabled={running} onClick={handleTrends}>
            Calculate trends
          </button>
        </div>
      </section>

      {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}
      {ingestResult ? (
        <pre className="food-normalization-page__summary">{JSON.stringify(ingestResult, null, 2)}</pre>
      ) : null}
      {trendResult ? (
        <pre className="food-normalization-page__summary">{JSON.stringify(trendResult, null, 2)}</pre>
      ) : null}
    </div>
  );
};

export default FoodSupplyIngestionPage;
